import type { ImageRef, Objective, Position } from "../studio/model.js";

/**
 * « Préparer un carrousel » (docs/PREPARER_UN_CARROUSEL.md), assisted mode: the Atelier
 * prepares the context, the members research, write and make the images in their own
 * conversations, then the Atelier imports the structured answer, checks it and builds an
 * editable project. Nothing here generates text or images.
 *
 * A preparation lives inside its Atelier project (StudioDoc.preparation): same save,
 * versions, conflicts, local draft, project file and sharing between members. This module
 * holds its shape and makes a stored one safe to read (normalizePreparation).
 */
export const PREPARATION_VERSION = 1;
/** The answer the dossier asks for; /1 answers (without cover mechanisms) are still read. */
export const ENVELOPE_FORMAT = "opulancia-preparation/2";

/** The rules a preparation was checked against, stored with it (shown when it is reopened). */
export const RULES_VERSION = "opulancia-regles-f1/3";
export const RULES_SOURCES = [
  "docs/EDITORIAL_CALIBRATION.md (bibliothèque mesurée le 2026-10-10)",
  "docs/VISUAL_CALIBRATION.md",
  "docs/visual-references/SIGNATURE_V0_2.md (§ 7 : Anton 400, Inter 600/700, ocre #E0A84A, monogramme)",
  "apps/renderer/src/editorial/qa.ts (contrôles éditoriaux du moteur, profil manual)",
  "apps/renderer/src/editorial/instructions.ts (consignes d'écriture et de couverture communes au moteur et au dossier)",
  "packages/schemas/carousel-preparation.schema.json (enveloppe opulancia-preparation/1)",
];

export type Stage = "brief" | "imported" | "built";
export type ReferenceUse = "reprendre" | "adapter" | "eviter";
export type AnalysisState = "current" | "stale" | "incomplete" | "none";

export const USE_LABEL: Record<ReferenceUse, string> = { reprendre: "À reprendre", adapter: "À adapter", eviter: "À éviter" };

/** What the end slide asks for, in the three vocabularies that meet here. */
export const OBJECTIVE_INFO: Record<Objective, { label: string; request: string; cta: "follow" | "send" | "save" | "comment"; style: "follow" | "share" | "save" | "comment" }> = {
  follow: { label: "Abonnement (faire suivre le compte)", request: "profile_to_follow", cta: "follow", style: "follow" },
  share: { label: "Partage (envoyer à quelqu'un)", request: "shares", cta: "send", style: "share" },
  save: { label: "Enregistrement (garder pour plus tard)", request: "retention", cta: "save", style: "save" },
  question: { label: "Commentaire (répondre à une question)", request: "community", cta: "comment", style: "comment" },
};

/** The outline's call to action as the Atelier's end slide: send → share, comment → question. */
export const CTA_TO_OBJECTIVE: Record<string, Objective> = { follow: "follow", send: "share", save: "save", comment: "question" };

export const METRICS = ["reach", "views", "likes", "comments", "shares", "saves", "profile_visits", "followers_gained"] as const;
export type Metric = (typeof METRICS)[number];
export const METRIC_LABEL: Record<Metric, string> = {
  reach: "Couverture",
  views: "Vues",
  likes: "J'aime",
  comments: "Commentaires",
  shares: "Partages",
  saves: "Enregistrements",
  profile_visits: "Visites du profil",
  followers_gained: "Abonnés gagnés",
};

/** A library deposit chosen as a reference, as read when it was chosen (refreshable). */
export interface ReferenceSnapshot {
  deposit_id: string;
  number: number;
  use: ReferenceUse;
  reason: string;
  slide_count: number;
  instagram_url: string | null;
  /** The stored cover (first slide): its path in the private store and its sha256. Never a URL. */
  cover: { storage_path: string; sha256: string; media_type: string } | null;
  analysis: {
    state: AnalysisState;
    analyzer_version: string | null;
    created_at: string | null;
    limits: string[];
    /** Text read by OCR on the slides, line by line: not a transcription. */
    ocr: Array<{ slide: number | null; text: string; uncertain: boolean }>;
    /** Slides whose text could not be read (failed or not run). */
    unread: number;
  };
  stats: {
    matched: boolean;
    captured_at: string | null;
    source: string | null;
    publication_type: string | null;
    published_local: string | null;
    /** False when the publication time zone is not confirmed (no age computed). */
    age_known: boolean;
    metrics: Record<Metric, number | null>;
  } | null;
  read_at: string;
}

export type ReviewMethod = "source_primaire" | "source_secondaire" | "document_officiel" | "autre";
export const METHOD_LABEL: Record<ReviewMethod, string> = {
  source_primaire: "Source primaire ouverte, passage lu",
  source_secondaire: "Source secondaire ouverte, passage lu",
  document_officiel: "Document officiel ou jeu de données ouvert, passage lu",
  autre: "Autre méthode (décrite dans la note)",
};

/** A member's reading of one claim: the only thing that makes a fact « confirmé ». */
export interface ClaimReview {
  claim_id: string;
  /** Fingerprint of the claim's text when it was read: a changed text needs a new reading. */
  claim_hash: string;
  decision: "confirmed" | "rejected";
  method: ReviewMethod;
  source_url: string;
  /** The passage the member read, copied from the source. */
  excerpt: string;
  note: string;
  by: string;
  at: string;
}

export interface Signoff {
  by: string;
  at: string;
  /** Fingerprint of what was reviewed: a later change asks for a new review. */
  fingerprint: string;
}

/**
 * The cover checklist (version 2): the nine questions of the first version and the method of
 * docs/PREPARER_UN_CARROUSEL.md (« Couvertures ») merged. Asked by a person, never scored. The
 * ids of version 1 keep their meaning, so a check recorded before stays readable.
 */
export const COVER_CHECKS = [
  ["subject", "Le sujet est compris immédiatement"],
  ["intrigue", "Ce qui surprend se voit dans l'image (situation, contraste, anomalie)"],
  ["promise", "Le titre ajoute une promesse claire, sans donner la réponse"],
  ["coherence", "L'image et la promesse du titre vont ensemble"],
  ["delivered", "Les slides livrent la promesse du titre"],
  ["accuracy", "Le sujet représenté est exact et crédible (pas un autre lieu, objet ou personne, rien d'exagéré)"],
  ["layout", "Le titre et le logo sont à leur place"],
  ["crop", "Le détail essentiel reste visible après recadrage, titre et dégradé"],
  ["phone", "Lisible sur téléphone et en vignette"],
  ["signature", "La signature OPULANCIA est respectée"],
] as const;
export type CoverCheckId = (typeof COVER_CHECKS)[number][0];
export const COVER_CHECKS_VERSION = 2;
const COVER_CHECK_IDS_V1: readonly CoverCheckId[] = ["subject", "intrigue", "coherence", "delivered", "accuracy", "layout", "crop", "phone", "signature"];
/** The questions of a checklist version (a check is complete when all of its own are answered yes). */
export const coverCheckIds = (version: number): readonly CoverCheckId[] => (version >= 2 ? COVER_CHECKS.map(([id]) => id) : COVER_CHECK_IDS_V1);
/** Questions that need the image: without one they are « non vérifiable ». */
export const IMAGE_CHECKS: ReadonlySet<CoverCheckId> = new Set(["subject", "intrigue", "coherence", "accuracy", "crop", "phone"]);

export interface CoverCheck extends Signoff {
  items: Record<CoverCheckId, boolean>;
  note: string;
  /** Checklist version (absent: 1, the nine questions). */
  version: number;
}

export type CheckAnswer = "oui" | "non" | "incertain";

/** The cover's framing, as the editor sets it (same bounds as a slide). */
export interface CoverFrame {
  fx: number;
  fy: number;
  zoom: number;
  darken: number;
  fade: number;
  /** How dark the fade gets (1: the generator's fade). */
  fadeStrength: number;
  /** Where the title sits; « bottom » is the Signature's place. */
  position: Position;
  /** null: the template's size. */
  titleSize: number | null;
}

/** One of the three concepts, as compared before the project is built. */
export interface CoverConcept {
  /** Index of the answer's hook (title, accent, visual concept, mechanism, surprise, brief). */
  hook_index: number;
  /** Optional: a member's image, stored once by sha256 (never generated here). */
  image: ImageRef | null;
  frame: CoverFrame;
  /** A person's answers to the checklist; image questions stay unanswered without an image. */
  answers: Partial<Record<CoverCheckId, CheckAnswer>>;
  /** What was understood in the one-second glance, as written by the member. */
  glance: string;
  notes: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface CoverDecision {
  /** Hook index of the concept kept. */
  chosen: number;
  /** Why (required). */
  reason: string;
  /** The concepts set aside, with their reason when one was given. */
  rejected: Array<{ hook_index: number; reason: string }>;
  by: string;
  at: string;
}

/** The comparison of the three cover concepts (kept with the project, the choice and the rejected ones included). */
export interface CoverStudy {
  checklist_version: number;
  concepts: CoverConcept[];
  decision: CoverDecision | null;
}

/** A new concept starts without fade: the member adds what the image needs (the contrast is measured). */
export const DEFAULT_FRAME: CoverFrame = { fx: 50, fy: 50, zoom: 1, darken: 0, fade: 0, fadeStrength: 1, position: "bottom", titleSize: null };

/**
 * The name a preparation's project gets automatically: the idea's first line, else the link.
 * While the project is not built, it follows the idea; a name a member typed is kept.
 */
export const autoProjectName = (idea: string, url: string | null) => (idea.trim().split("\n")[0]!.trim() || url || "Préparation").slice(0, 100);

/** The project name after the idea or the link changed: renamed only when it was still the automatic one. */
export function syncedProjectName(current: string, before: { idea: string; url: string | null }, after: { idea: string; url: string | null }): string {
  return current === autoProjectName(before.idea, before.url) ? autoProjectName(after.idea, after.url) : current;
}

export function newCoverStudy(): CoverStudy {
  return {
    checklist_version: COVER_CHECKS_VERSION,
    concepts: [0, 1, 2].map((i) => ({ hook_index: i, image: null, frame: { ...DEFAULT_FRAME }, answers: {}, glance: "", notes: "", updated_by: null, updated_at: null })),
    decision: null,
  };
}

/** Which scenario slide an Atelier slide comes from, with its brief (kept while images are added). */
export interface SlideLink {
  slide_id: string;
  outline_index: number;
  role: string;
  claim_ids: string[];
  brief: Record<string, unknown> | null;
}

export interface Preparation {
  version: typeof PREPARATION_VERSION;
  id: string;
  rules_version: string;
  created_at: string;
  created_by: string;
  stage: Stage;
  idea: string;
  /** An inspiration link, kept as a reference to open or provide: its content is never read here. */
  inspiration_url: string | null;
  objective: Objective;
  format: "F1";
  references: ReferenceSnapshot[];
  /** The structured answer as imported (statuses as the conversation wrote them). */
  envelope: Record<string, unknown> | null;
  imported_at: string | null;
  imported_by: string | null;
  /** Index of the chosen cover among the envelope's three hooks. */
  cover_hook: number;
  claim_reviews: ClaimReview[];
  review: Signoff | null;
  cover_check: CoverCheck | null;
  /** The cover comparator (absent until a member uses it). */
  cover_study: CoverStudy | null;
  slide_links: SlideLink[];
  built_at: string | null;
  built_by: string | null;
}

export const PREP_LIMITS = { idea: 1000, url: 500, reason: 400, references: 3, ocrLines: 60, ocrText: 200, excerpt: 2000, note: 600, limits: 12 } as const;

/** A candidate id of the editorial contracts (CAND-YYYY-NNNNN). */
export function newPreparationId(now = new Date()): string {
  const n = 10000 + Math.floor(Math.random() * 90000);
  return `CAND-${now.getUTCFullYear()}-${n}`;
}

export function newPreparation(input: { idea: string; inspiration_url: string | null; objective: Objective; by: string; now?: Date }): Preparation {
  const now = input.now ?? new Date();
  return {
    version: PREPARATION_VERSION,
    id: newPreparationId(now),
    rules_version: RULES_VERSION,
    created_at: now.toISOString(),
    created_by: input.by,
    stage: "brief",
    idea: input.idea.slice(0, PREP_LIMITS.idea),
    inspiration_url: input.inspiration_url,
    objective: input.objective,
    format: "F1",
    references: [],
    envelope: null,
    imported_at: null,
    imported_by: null,
    cover_hook: 0,
    claim_reviews: [],
    review: null,
    cover_check: null,
    cover_study: null,
    slide_links: [],
    built_at: null,
    built_by: null,
  };
}

/** A link the member may keep as an inspiration: http(s) only; anything else is refused. */
export function inspirationUrl(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  try {
    const url = new URL(v);
    if (url.protocol !== "https:" && url.protocol !== "http:") throw new Error();
    return url.toString().slice(0, PREP_LIMITS.url);
  } catch {
    throw new Error("Lien d'inspiration invalide (attendu : une adresse https://…).");
  }
}

/** cyrb53: a short, stable fingerprint (not a security hash) to notice that a text changed. */
export function fingerprint(value: unknown): string {
  const text = typeof value === "string" ? value : JSON.stringify(value);
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, "0");
}

const str = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");
const strOrNull = (v: unknown, max: number) => (typeof v === "string" && v ? v.slice(0, max) : null);
const oneOf = <T extends string>(v: unknown, values: readonly T[], fallback: T): T => (values.includes(v as T) ? (v as T) : fallback);
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const count = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) && v >= 0 ? v : null);

function normalizeReference(value: unknown): ReferenceSnapshot | null {
  const r = obj(value);
  const number = count(r["number"]);
  if (typeof r["deposit_id"] !== "string" || !number) return null;
  const cover = obj(r["cover"]);
  const analysis = obj(r["analysis"]);
  const stats = r["stats"] ? obj(r["stats"]) : null;
  const metrics = obj(stats?.["metrics"]);
  return {
    deposit_id: r["deposit_id"].slice(0, 64),
    number,
    use: oneOf(r["use"], ["reprendre", "adapter", "eviter"] as const, "adapter"),
    reason: str(r["reason"], PREP_LIMITS.reason),
    slide_count: count(r["slide_count"]) ?? 0,
    instagram_url: strOrNull(r["instagram_url"], PREP_LIMITS.url),
    cover:
      typeof cover["storage_path"] === "string" && typeof cover["sha256"] === "string" && /^[0-9a-f]{64}$/.test(cover["sha256"])
        ? { storage_path: cover["storage_path"].slice(0, 300), sha256: cover["sha256"], media_type: str(cover["media_type"], 40) || "image/jpeg" }
        : null,
    analysis: {
      state: oneOf(analysis["state"], ["current", "stale", "incomplete", "none"] as const, "none"),
      analyzer_version: strOrNull(analysis["analyzer_version"], 40),
      created_at: strOrNull(analysis["created_at"], 40),
      limits: arr(analysis["limits"]).filter((l): l is string => typeof l === "string").slice(0, PREP_LIMITS.limits).map((l) => l.slice(0, 300)),
      ocr: arr(analysis["ocr"])
        .slice(0, PREP_LIMITS.ocrLines)
        .map((l) => obj(l))
        .filter((l) => typeof l["text"] === "string")
        .map((l) => ({ slide: count(l["slide"]), text: str(l["text"], PREP_LIMITS.ocrText), uncertain: l["uncertain"] === true })),
      unread: count(analysis["unread"]) ?? 0,
    },
    stats: stats
      ? {
          matched: stats["matched"] === true,
          captured_at: strOrNull(stats["captured_at"], 40),
          source: strOrNull(stats["source"], 40),
          publication_type: strOrNull(stats["publication_type"], 40),
          published_local: strOrNull(stats["published_local"], 40),
          age_known: stats["age_known"] === true,
          metrics: Object.fromEntries(METRICS.map((m) => [m, count(metrics[m])])) as Record<Metric, number | null>,
        }
      : null,
    read_at: str(r["read_at"], 40),
  };
}

const clampNum = (v: unknown, lo: number, hi: number, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback);

function normalizeImage(value: unknown): ImageRef | null {
  const img = obj(value);
  if (typeof img["sha256"] !== "string" || !/^[0-9a-f]{64}$/.test(img["sha256"])) return null;
  return {
    sha256: img["sha256"],
    type: oneOf(img["type"], ["image/jpeg", "image/png", "image/webp"] as const, "image/jpeg"),
    width: clampNum(img["width"], 1, 20000, 1080),
    height: clampNum(img["height"], 1, 20000, 1350),
  };
}

function normalizeCoverStudy(value: unknown): CoverStudy | null {
  if (!value || typeof value !== "object") return null;
  const v = obj(value);
  const given = arr(v["concepts"]).map(obj);
  const concepts = [0, 1, 2].map((i): CoverConcept => {
    const c = given.find((x) => x["hook_index"] === i) ?? {};
    const f = obj(c["frame"]);
    const answers = obj(c["answers"]);
    return {
      hook_index: i,
      image: normalizeImage(c["image"]),
      frame: {
        fx: clampNum(f["fx"], 0, 100, 50),
        fy: clampNum(f["fy"], 0, 100, 50),
        zoom: clampNum(f["zoom"], 1, 3, 1),
        darken: clampNum(f["darken"], 0, 0.8, 0),
        // a frame saved before the default changed keeps its own fade
        fade: clampNum(f["fade"], 0, 1, 1),
        fadeStrength: clampNum(f["fadeStrength"], 0, 1, 1),
        position: f["position"] === "top" || f["position"] === "center" ? f["position"] : "bottom",
        titleSize: typeof f["titleSize"] === "number" && Number.isFinite(f["titleSize"]) ? Math.min(200, Math.max(28, f["titleSize"])) : null,
      },
      answers: Object.fromEntries(COVER_CHECKS.flatMap(([k]) => (["oui", "non", "incertain"].includes(answers[k] as string) ? [[k, answers[k] as CheckAnswer]] : []))),
      glance: str(c["glance"], PREP_LIMITS.note),
      notes: str(c["notes"], PREP_LIMITS.note),
      updated_by: strOrNull(c["updated_by"], 64),
      updated_at: strOrNull(c["updated_at"], 40),
    };
  });
  const d = obj(v["decision"]);
  const chosen = count(d["chosen"]);
  const decision: CoverDecision | null =
    chosen !== null && chosen <= 2 && typeof d["reason"] === "string" && d["reason"].trim() && typeof d["by"] === "string"
      ? {
          chosen,
          reason: str(d["reason"], PREP_LIMITS.reason),
          rejected: arr(d["rejected"])
            .map(obj)
            .filter((r) => count(r["hook_index"]) !== null && (r["hook_index"] as number) <= 2 && r["hook_index"] !== chosen)
            .slice(0, 2)
            .map((r) => ({ hook_index: r["hook_index"] as number, reason: str(r["reason"], PREP_LIMITS.reason) })),
          by: str(d["by"], 64),
          at: str(d["at"], 40),
        }
      : null;
  return { checklist_version: count(v["checklist_version"]) ?? COVER_CHECKS_VERSION, concepts, decision };
}

function normalizeSignoff(value: unknown): Signoff | null {
  const s = obj(value);
  return typeof s["by"] === "string" && typeof s["at"] === "string" && typeof s["fingerprint"] === "string" ? { by: s["by"].slice(0, 64), at: s["at"].slice(0, 40), fingerprint: s["fingerprint"].slice(0, 40) } : null;
}

/** A preparation read from a stored document, a draft or a project file, made safe to show. */
export function normalizePreparation(input: unknown): Preparation | null {
  if (!input || typeof input !== "object") return null;
  const p = input as Record<string, unknown>;
  if (p["version"] !== PREPARATION_VERSION) throw new Error(`Version de préparation inconnue : ${String(p["version"])}`);
  const id = typeof p["id"] === "string" && /^CAND-[0-9]{4}-[0-9]{5}$/.test(p["id"]) ? p["id"] : newPreparationId();
  const signoff = normalizeSignoff(p["cover_check"]);
  const coverItems = obj(obj(p["cover_check"])["items"]);
  const envelope = p["envelope"] && typeof p["envelope"] === "object" && !Array.isArray(p["envelope"]) ? structuredClone(p["envelope"] as Record<string, unknown>) : null;
  return {
    version: PREPARATION_VERSION,
    id,
    rules_version: str(p["rules_version"], 60) || RULES_VERSION,
    created_at: str(p["created_at"], 40),
    created_by: str(p["created_by"], 64),
    stage: oneOf(p["stage"], ["brief", "imported", "built"] as const, envelope ? "imported" : "brief"),
    idea: str(p["idea"], PREP_LIMITS.idea),
    inspiration_url: strOrNull(p["inspiration_url"], PREP_LIMITS.url),
    objective: oneOf(p["objective"], ["follow", "share", "save", "question"] as const, "follow"),
    format: "F1",
    references: arr(p["references"])
      .map(normalizeReference)
      .filter((r): r is ReferenceSnapshot => r !== null)
      .slice(0, PREP_LIMITS.references),
    envelope,
    imported_at: strOrNull(p["imported_at"], 40),
    imported_by: strOrNull(p["imported_by"], 64),
    cover_hook: count(p["cover_hook"]) !== null && (p["cover_hook"] as number) <= 2 ? (p["cover_hook"] as number) : 0,
    claim_reviews: arr(p["claim_reviews"])
      .map((v) => obj(v))
      .filter((r) => typeof r["claim_id"] === "string" && typeof r["by"] === "string" && typeof r["excerpt"] === "string" && r["excerpt"])
      .slice(0, 200)
      .map((r) => ({
        claim_id: str(r["claim_id"], 60),
        claim_hash: str(r["claim_hash"], 40),
        decision: oneOf(r["decision"], ["confirmed", "rejected"] as const, "rejected"),
        method: oneOf(r["method"], ["source_primaire", "source_secondaire", "document_officiel", "autre"] as const, "autre"),
        source_url: str(r["source_url"], PREP_LIMITS.url),
        excerpt: str(r["excerpt"], PREP_LIMITS.excerpt),
        note: str(r["note"], PREP_LIMITS.note),
        by: str(r["by"], 64),
        at: str(r["at"], 40),
      })),
    review: normalizeSignoff(p["review"]),
    cover_check: signoff
      ? {
          ...signoff,
          note: str(obj(p["cover_check"])["note"], PREP_LIMITS.note),
          items: Object.fromEntries(COVER_CHECKS.map(([k]) => [k, coverItems[k] === true])) as Record<CoverCheckId, boolean>,
          version: obj(p["cover_check"])["version"] === 2 ? 2 : 1,
        }
      : null,
    cover_study: normalizeCoverStudy(p["cover_study"]),
    slide_links: arr(p["slide_links"])
      .map((v) => obj(v))
      .filter((l) => typeof l["slide_id"] === "string" && count(l["outline_index"]) !== null)
      .slice(0, 40)
      .map((l) => ({
        slide_id: str(l["slide_id"], 40),
        outline_index: l["outline_index"] as number,
        role: str(l["role"], 20),
        claim_ids: arr(l["claim_ids"]).filter((c): c is string => typeof c === "string").slice(0, 20),
        brief: l["brief"] && typeof l["brief"] === "object" ? structuredClone(l["brief"] as Record<string, unknown>) : null,
      })),
    built_at: strOrNull(p["built_at"], 40),
    built_by: strOrNull(p["built_by"], 64),
  };
}
